import { CarregamentoService } from './../service/carregamento/carregamento.service';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class BaseApi {
  protected http = inject(HttpClient);
  protected carregamentoService = inject(CarregamentoService);

  private readonly urlApi = environment.apiUrl;

  private withLoading<T>(obs$: Observable<T>): Observable<T> {
    this.carregamentoService.Ativar();
    return obs$.pipe(finalize(() => this.carregamentoService.Desativar()));
  }

  private createFormData(model: Record<string, unknown>): FormData {
    const formData = new FormData();
    for (const key in model) {
      formData.append(key, (model[key] ?? '') as string | Blob);
    }
    return formData;
  }

  private jsonHeaders(): HttpHeaders {
    return new HttpHeaders().set('Content-Type', 'application/json');
  }

  protected post<T>(url: string, model: unknown, headers?: HttpHeaders, params?: HttpParams): Observable<T> {
    return this.withLoading(this.http.post<T>(this.urlApi + url, model, { headers, params }));
  }

  protected postWithoutLoading<T>(url: string, model: unknown, headers?: HttpHeaders, params?: HttpParams): Observable<T> {
    return this.http.post<T>(this.urlApi + url, model, { headers, params });
  }

  protected postForm<T>(url: string, model: Record<string, unknown>): Observable<T> {
    const formData = this.createFormData(model);
    return this.withLoading(this.http.post<T>(this.urlApi + url, formData));
  }

  protected postFormWithoutLoading<T>(url: string, model: Record<string, unknown>): Observable<T> {
    const formData = this.createFormData(model);
    return this.http.post<T>(this.urlApi + url, formData);
  }

  protected postMultipart<T>(url: string, model: Record<string, unknown>): Observable<T> {
    const formData = this.createFormData(model);
    const headers = new HttpHeaders({ Accept: 'application/json' });
    return this.withLoading(
      this.http.post<T>(this.urlApi + url, formData, { headers }).pipe(catchError((e) => throwError(() => e))),
    );
  }

  protected postJson<T>(url: string, model: unknown): Observable<T> {
    return this.withLoading(
      this.http.post<T>(this.urlApi + url, JSON.stringify(model), { headers: this.jsonHeaders() })
        .pipe(catchError((e) => throwError(() => e))),
    );
  }

  protected postJsonWithoutLoading<T>(url: string, model: unknown): Observable<T> {
    return this.http.post<T>(this.urlApi + url, JSON.stringify(model), { headers: this.jsonHeaders() })
      .pipe(catchError((e) => throwError(() => e)));
  }

  protected get<T>(url: string, params?: HttpParams): Observable<T> {
    return this.withLoading(this.http.get<T>(this.urlApi + url, { params }));
  }

  protected getJson<T>(url: string): Observable<T> {
    return this.withLoading(
      this.http.get<T>(this.urlApi + url, { headers: this.jsonHeaders() })
        .pipe(catchError((e) => throwError(() => e))),
    );
  }

  protected getWithoutLoading<T>(url: string, params?: HttpParams): Observable<T> {
    return this.http.get<T>(this.urlApi + url, { params }).pipe(catchError((e) => throwError(() => e)));
  }

  protected getJsonWithoutLoading<T>(url: string): Observable<T> {
    return this.http.get<T>(this.urlApi + url, { headers: this.jsonHeaders() })
      .pipe(catchError((e) => throwError(() => e)));
  }

  protected pesquisar<T>(formData: FormData, url: string, params?: HttpParams): Observable<T> {
    return this.withLoading(this.http.post<T>(this.urlApi + url, formData, { params }));
  }

  protected pesquisarWithoutLoading<T>(formData: FormData, url: string, params?: HttpParams): Observable<T> {
    return this.http.post<T>(this.urlApi + url, formData, { params });
  }

  protected postProgress(url: string, file: File): Observable<unknown> {
    const formData = new FormData();
    formData.append('arquivo', file);
    return this.http.post(this.urlApi + url, formData, { reportProgress: true, observe: 'events' });
  }
}
